<script setup>
import { useModal } from '@/composables/useModal.js';
import { IconInfoCircleFilled } from '@tabler/icons-vue';
import SlideOver from '@/components/SlideOver.vue';
import LinkListDisplay from '@/components/pageSpecific/LinkListDisplay.vue';
import Tabs from '@/components/Tabs.vue';
import Tab from '@/components/Tab.vue';
import { ref, watch } from 'vue';
import AppCard from '../../../../../Shared/AppCard.vue';
import QuestionBaseDisplay from '@/components/Quiz/QuestionBaseDisplay.vue';
import QuestionDescriptionDisplay from '@/components/Quiz/QuestionDescriptionDisplay.vue';
import CopyrightNotice from '@/components/CopyrightNotice.vue';
import QuestionLineGraph from '@/components/Quiz/QuestionLineGraph.vue';
import HintAlert from '@/components/Quiz/HintAlert.vue';
import BadgePill from '@/components/BadgePill.vue';
import { useAxios } from '@vueuse/integrations/useAxios';
import GraphToolbox from '../../../../../components/Quiz/GraphToolbox.vue';
import GraphFunctionPlot from '../../../../../components/Quiz/GraphFunctionPlot.vue';

const props = defineProps({
    tag: {
        required: true,
        type: Object,
    },
});

const emit = defineEmits(['close']);
const { isOpen, dismiss } = useModal(false);
const close = () => {
    emit('close');
    dismiss();
};

const questions = ref([]);

watch(
    () => isOpen.value,
    () => {
        if (isOpen.value === true && questions.value.length === 0) {
            execute();
        }
    },
);

const { execute, isLoading, data } = useAxios(props.tag.urls.samples_index, { method: 'GET' }, { immediate: false });
watch(data, (newVal) => {
    questions.value = newVal.items;
});
</script>

<template>
    <div class="relative inline-block text-left">
        <button @click="isOpen = !isOpen" type="button">
            <IconInfoCircleFilled class="w-6 h-6 text-gray-400" />
        </button>
        <SlideOver :show="isOpen" @close="close" size="xxxxxl">
            <Tabs>
                <Tab :title="$t('messages.sample_questions')" :selected="true">
                    <div v-if="isLoading" class="flex justify-center">
                        <div class="border-gray-300 h-20 w-20 animate-spin rounded-full border-8 border-t-blue-600" />
                    </div>
                    <div class="flex flex-col gap-4 mt-5" v-auto-animate>
                        <AppCard v-for="question in questions" :key="question.id">
                            <QuestionBaseDisplay :question="question" class="p-2">
                                <div>
                                    <div class="font-semibold text-gray-900">
                                        {{ question.title }}
                                    </div>
                                    <div class="mt-3 p-2 bg-gray-100 rounded-lg" v-if="question.description">
                                        <QuestionDescriptionDisplay :question="question" />
                                    </div>
                                    <div class="mt-3 p-2 bg-gray-100 rounded-lg" v-if="question.image_path">
                                        <img :src="question.image_path" alt="cover" class="h-auto" />
                                    </div>
                                    <div class="mt-3 p-2 bg-gray-100 rounded-lg" v-if="question.shared_image">
                                        <img :src="question.shared_image" alt="cover" class="h-auto" />
                                    </div>
                                    <div>
                                        <CopyrightNotice :info="question.copyright" />
                                    </div>
                                    <div class="mt-3 p-2" v-if="question.graph">
                                        <QuestionLineGraph
                                            :data="question.graph"
                                            v-if="question.graph.type === 'R1Did'"
                                        />
                                        <GraphToolbox
                                            :elements="question.graph.elements"
                                            :graph="question.graph.graph"
                                            v-if="question.graph.type === 'GraphToolbox'"
                                        />
                                        <GraphFunctionPlot
                                            :elements="question.graph.elements"
                                            :graph="question.graph.graph"
                                            v-if="question.graph.type === 'GraphFunctionPlot'"
                                        />
                                    </div>

                                    <HintAlert v-if="question.hint" class="mt-1">
                                        <span class="font-semibold">{{ $t('messages.hint') }}:</span>
                                        {{ question.hint }}
                                    </HintAlert>

                                    <component :is="question.type" :question="question" />
                                </div>
                                <div class="mt-1 flex flex-wrap gap-2">
                                    <span v-for="tag in question.tag_links" :key="tag.id" :href="tag.url">
                                        <BadgePill>
                                            {{ tag.name }}
                                        </BadgePill>
                                    </span>
                                </div>
                                <div class="mt-1 flex flex-wrap gap-2">
                                    <BadgePill
                                        v-for="grade_level in question.grade_levels_display"
                                        :key="grade_level.id"
                                        color="gray"
                                    >
                                        {{ grade_level.name }}
                                    </BadgePill>
                                </div>
                                <div>
                                    <BadgePill color="gray">
                                        {{ question.subject_name }}
                                    </BadgePill>
                                </div>
                            </QuestionBaseDisplay>
                        </AppCard>
                    </div>
                </Tab>
                <Tab :title="$tc('messages.links', 2)" v-if="tag.links.length">
                    <div>
                        <LinkListDisplay :links="tag.links" />
                    </div>
                </Tab>
            </Tabs>
        </SlideOver>
    </div>
</template>
